import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import SwivuModal from "../../../react/components/editors/SwivuModal.jsx";
import EditorBase from "../base/EditorBase.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { useCallback } from "react";
import { getCompanyCamProjectList } from "../../../network/network.jsx";
import moment from "moment";
import { StringHelper } from "@bryntum/schedulerpro";

const COMPANY_CAM_PROJECT_LIST_PER_PAGE = 25;
const SCROLL_BUFFER = 25; // Buffer is increased to 25 here, because masonry layout is causing a bug with bryntum container where the clientHeight + scrollTop is still less then scroll height of container so to fix it keeping an increased buffer for scroll listeners

class SwivuCompanyCampProjectPopup extends EditorBase {
  static get $name() {
    return "SwivuCompanyCampProject";
  }

  static get type() {
    return WIDGET_KEYS.companyCamProjectType;
  }

  static get configurable() {
    return {
      title: "Company Cam Projects",
      width: "93em",
      minWidth: "30em",
      maxWidth: "100vw",
      height: "min(100vh, 800px)",
      minHeight: "600px",
      maxHeight: "100vh",
      closable: true,
      autoClose: false,
      draggable: true,
      scrollable: true,
      cls: "resize minimal-gradient-editor",
      stateId: PERSISTENCE_STATE_KEYS.companyCamProjectEditor,
      contentElementCls: "@container",
      items: {
        errorContainer: {
          type: "container",
          contentElementCls: "gap-[1.5em] w-full h-full",
          layoutStyle: {
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          },
          hidden: true,
          items: {
            errorMessage: {
              type: "displayfield",
              cls: "text-[1em] text-center max-w-[33em]"
            },
            errorRetryButton: {
              type: "button",
              text: "Retry",
              icon: "fa-classic fa-solid fa-rotate-reverse",
              cls: "bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md",
              minWidth: 166,
              onClick: "up.loadCompanyCamData"
            }
          }
        },
        projectListContainer: {
          type: "container",
          contentElementCls: "gap-[1em] w-full h-full",
          layoutStyle: {
            flexDirection: "column",
            flexWrap: "nowrap"
          },
          items: {
            projectSearchContainer: {
              type: "container",
              contentElementCls: "grid grid-cols-1 @md:grid-cols-2 @md:gap-0 gap-[1em] items-center",
              items: {
                projectSearchLabel: {
                  html: "Choose Company Cam Project"
                },
                projectSearchField: {
                  type: "text",
                  placeholder: "Search",
                  label: "Search",
                  labelCls: "hidden @md:block",
                  cls: "w-full @md:w-auto",
                  onChange: "up.onProjectSearch"
                }
              }
            },
            projectList: {
              type: "list",
              scrollable: {
                overflowY: "auto",
                listeners: {
                  scroll: "up.onScrollList"
                }
              },
              flex: "1 1 0%",
              itemTpl: (item) => StringHelper.xss`
                <div class="cc-list-item">
                  <img
                    src="${item.image_url || "https://ps.w.org/replace-broken-images/assets/icon-256x256.png"}"
                    alt="${item.name || "Untitled"}"
                  />
                  <div>
                    <div class="text-ellipsis whitespace-nowrap overflow-hidden text-[1.25em] mb-[0.5em]">${item.name ?? "Untitled"}</div>
                    <div class="text-ellipsis whitespace-nowrap overflow-hidden">${Object.values(item.address).filter(Boolean).join(", ")}</div>
                    <div class="flex flex-col @md:flex-row justify-between mt-[1em]">
                        <small>Last Updated: ${moment(item.updated_at * 1000).fromNow()}</small>
                        <small>Created By: ${item.creator_name}</small>
                    </div>
                  </div>
                </div>
              `,
              onItem: "up.onSelectProjectItem"
            }
          }
        }
      }
    };
  }

  editRecord(record, context, otherParams) {
    this.parentEntityRecord = record;
    this.otherParams = otherParams;
    super.editRecord(record, context, otherParams);
    this.companyCamToken = context.project.extraBoardIntegrationsStore.find((item) => item.key === "companyCam");
    this.loadProjectList();
  }

  loadProjectList() {
    const { projectList, projectSearchField } = this.widgetMap;
    this.isProjectListLoading = true;
    if (!this.projectsPageNumber) this.projectsPageNumber = 1;
    this.toggleMask();
    this.removeError();

    this.changeCrossToEsc();

    getCompanyCamProjectList({ page: this.projectsPageNumber, query: projectSearchField.value }, this.companyCamToken.value)
      .then(({ data }) => {
        this.loadMore = data.length === COMPANY_CAM_PROJECT_LIST_PER_PAGE;
        const listItems = data.map((project) => ({
          id: project.id,
          name: project.name,
          address: project.address,
          image_url: project.feature_image[2]?.url,
          creator_name: project.creator_name,
          updated_at: project.updated_at
        }));
        if (!projectList.items) projectList.items = [];
        projectList.items = projectList.items.concat(listItems);
        this.projectsPageNumber += 1;
      })
      .catch((err) => {
        console.log(err, "error");
        this.isProjectListLoading = false;
      })
      .finally(() => {
        this.toggleMask();
        this.isProjectListLoading = false;
      });
  }

  onSelectProjectItem({ record }) {
    if (this.parentEntityRecord.isPhantom) {
      this.parentEntityRecord.name = record.name || "Untitled";
      this.otherParams.addEntityRecordFromCompanyCamProject();
    }
    this.parentEntityRecord.companyCamProject = record.id;
    this.parentEntityRecord.hasCompanyCamKey = true;
    this.close();
  }

  onProjectSearch() {
    const { projectList } = this.widgetMap;
    projectList.items = [];
    this.projectsPageNumber = 1;
    this.loadProjectList();
  }

  onScrollList({ srcElement }) {
    const { scrollTop, clientHeight, scrollHeight } = srcElement;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_BUFFER && this.loadMore && !this.isProjectListLoading) {
      this.loadProjectList();
    }
  }

  toggleMask() {
    if (this.companyCamLoader) {
      if (this.companyCamLoader.isVisible) this.companyCamLoader.hide();
      else this.companyCamLoader.show();
    } else {
      this.companyCamLoader = this.mask("Loading...");
    }
  }

  removeError() {
    const { projectListContainer, errorContainer } = this.widgetMap;
    if (this.beforeErrorHiddenState) {
      projectListContainer.hidden = this.beforeErrorHiddenState.projectListContainer;
    }
    errorContainer.hidden = true;
  }
}

SwivuCompanyCampProjectPopup.initClass();

export const SwivuCompanyCamProjectModal = ({ record, context, onClose, isShow, editorInstance }) => {
  const onShowHandler = useCallback(
    (editor, otherParams = {}) => {
      editor.project = context.project;
      const { parentEntityRecord, addEntityRecordFromCompanyCamProject } = record;
      editor.editRecord(parentEntityRecord, context, { ...otherParams, addEntityRecordFromCompanyCamProject });
      editorInstance.editorRef = editor;
    },
    [context, editorInstance]
  );

  return <SwivuModal widgetType={SwivuCompanyCampProjectPopup.type} isShow={isShow} onHide={onClose} onShow={onShowHandler} />;
};
